import * as React from "react";
import {
  DisabilityInsurancePayload,
  DisabilityInsuranceWizard,
} from "@earned/wizard";
import useTasksStore from "@modules/tasks/state";
import { TaskTemplateId, WizardId } from "@modules/tasks/types";
import { useDisabilityInsuranceInfo } from "@modules/tasks/queries";
import { useUpdateDisabilityInsuranceInfo } from "@modules/tasks/mutations";
import { useWizardCloseConfirm } from "@modules/tasks/hooks.web";
import { NoCacheWizardExitMessage } from "@modules/tasks/constants";
interface DisabilityInsuranceWizardControllerProps {
  clients: Forme.ClientUser[];
}

const DisabilityInsuranceWizardController: React.FC<
  DisabilityInsuranceWizardControllerProps
> = ({ clients }) => {
  const {
    wizardId,
    setWizardId,
    isWizardOpen,
    setIsWizardOpen,
    wizardTask,
    setWizardTask,
    setRemoteCompleteTaskId,
  } = useTasksStore();
  const client = React.useMemo(() => {
    return clients.filter((c) =>
      c.isPrimary
        ? wizardTask?.templateId ===
          TaskTemplateId.UPDATE_DISABILITY_INSURANCE_INFORMATION
        : wizardTask?.templateId ===
          TaskTemplateId.UPDATE_DISABILITY_INSURANCE_INFORMATION_COCLIENT
    )[0] as Forme.ClientUser;
  }, [clients, wizardTask]);
  const { disabilityInsuranceInfo, isLoading } = useDisabilityInsuranceInfo(
    client.id
  );
  const {
    mutateAsync,
    isLoading: isSubmitting,
    isSuccess,
    reset,
  } = useUpdateDisabilityInsuranceInfo(client.id);

  const isOpen = React.useMemo(
    () => wizardId === WizardId.DISABILITY_INSURANCE && isWizardOpen,
    [wizardId, isWizardOpen]
  );

  const { openCloseConfirmDialog, CloseConfirmDialogComponent } =
    useWizardCloseConfirm({
      onConfirmClose: reset,
      dialogMessage: NoCacheWizardExitMessage,
    });

  const handleSubmit = React.useCallback(
    async (payload: DisabilityInsurancePayload) => {
      await mutateAsync({ data: payload });
    },
    [mutateAsync]
  );

  const handleDone = React.useCallback(async () => {
    if (wizardTask) setRemoteCompleteTaskId(wizardTask.id);
    setWizardTask(null);
    setWizardId(null);
    reset();
  }, [reset, setWizardId, setWizardTask, setRemoteCompleteTaskId, wizardTask]);

  return (
    <>
      <DisabilityInsuranceWizard
        infoOwnerName={
          clients.length === 1
            ? undefined
            : `${client.firstName} ${client.lastName}`
        }
        isOpen={isOpen}
        close={openCloseConfirmDialog}
        onSubmit={handleSubmit}
        isSuccess={isSuccess}
        isSubmitting={isSubmitting}
        isLoading={isLoading}
        onDone={handleDone}
        cache={disabilityInsuranceInfo}
        modalRootSelector="#wizard-root"
      />
      <CloseConfirmDialogComponent />
    </>
  );
};

export default DisabilityInsuranceWizardController;
